import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { 
  Home, ShoppingCart, User, LogOut, Shield, CreditCard, Users, Search, 
  XCircle, RefreshCw, Calendar, DollarSign, Clock, CheckCircle, AlertCircle
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_91be65d6-9fee-4eda-abde-3fd83cab62ae/artifacts/kxr3j7eu_IMG_7793.png";

export default function AdminBLKCRD() {
  const { user, logout, getAuthHeader } = useAuth();
  const { itemCount } = useCart();
  
  // BLKCRD state
  const [blkcrdUsers, setBlkcrdUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [blkcrdSearch, setBlkcrdSearch] = useState('');
  const [blkcrdSearchResults, setBlkcrdSearchResults] = useState([]);
  const [searchingBlkcrd, setSearchingBlkcrd] = useState(false);
  const [stats, setStats] = useState({
    total_members: 0,
    active_members: 0,
    expired_members: 0,
    total_savings: 0
  });

  useEffect(() => {
    fetchBlkcrdUsers();
  }, []);

  const fetchBlkcrdUsers = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/blkcrd/users`, { headers: getAuthHeader() });
      if (res.ok) {
        const data = await res.json();
        const users = data.users || [];
        setBlkcrdUsers(users);
        
        // Calculate stats
        const now = new Date();
        const active = users.filter(u => !u.expires_at || new Date(u.expires_at) > now).length;
        const expired = users.filter(u => u.expires_at && new Date(u.expires_at) <= now).length;
        const totalSavings = users.reduce((sum, u) => sum + (u.blkcrd_total_savings || 0), 0);
        
        setStats({
          total_members: users.length,
          active_members: active,
          expired_members: expired,
          total_savings: totalSavings
        });
      }
    } catch (error) {
      console.error('Failed to fetch BLKCRD users:', error);
      toast.error('FAILED TO LOAD BLKCRD MEMBERS');
    } finally {
      setLoading(false);
    }
  };

  const searchBlkcrdUsers = async (query) => {
    if (query.length < 2) {
      setBlkcrdSearchResults([]);
      return;
    }
    setSearchingBlkcrd(true);
    try {
      const res = await fetch(`${API}/api/admin/blkcrd/search?q=${encodeURIComponent(query)}`, { 
        headers: getAuthHeader() 
      });
      if (res.ok) {
        const data = await res.json();
        setBlkcrdSearchResults(data.users || []);
      }
    } catch (error) {
      console.error('Failed to search users:', error);
    } finally {
      setSearchingBlkcrd(false);
    }
  };

  const grantBlkcrd = async (userId, userEmail) => {
    try {
      const res = await fetch(`${API}/api/admin/blkcrd/grant/${userId}`, {
        method: 'POST',
        headers: getAuthHeader()
      });
      if (res.ok) {
        toast.success(`BLKCRD GRANTED TO ${userEmail.toUpperCase()}`);
        fetchBlkcrdUsers();
        setBlkcrdSearch('');
        setBlkcrdSearchResults([]);
      } else {
        const data = await res.json();
        toast.error(data.detail || 'FAILED TO GRANT BLKCRD');
      }
    } catch (error) {
      toast.error('FAILED TO GRANT BLKCRD');
    }
  };

  const revokeBlkcrd = async (userId, userEmail) => {
    if (!window.confirm(`Revoke BLKCRD from ${userEmail}?`)) return;
    try {
      const res = await fetch(`${API}/api/admin/blkcrd/revoke/${userId}`, {
        method: 'POST',
        headers: getAuthHeader()
      });
      if (res.ok) {
        toast.success(`BLKCRD REVOKED FROM ${userEmail.toUpperCase()}`);
        fetchBlkcrdUsers();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'FAILED TO REVOKE BLKCRD');
      }
    } catch (error) {
      toast.error('FAILED TO REVOKE BLKCRD');
    }
  };

  const setBlkcrdStatus = async (userId, userEmail, newStatus) => {
    const action = newStatus === 'active' ? 'REACTIVATE' : 'EXPIRE';
    if (!window.confirm(`${action} BLKCRD for ${userEmail}?`)) return;
    try {
      const res = await fetch(`${API}/api/admin/blkcrd/set-status/${userId}`, {
        method: 'PUT',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        toast.success(`BLKCRD ${action}D FOR ${userEmail.toUpperCase()}`);
        fetchBlkcrdUsers();
      } else {
        const data = await res.json();
        toast.error(data.detail || `FAILED TO ${action} BLKCRD`);
      }
    } catch (error) {
      toast.error(`FAILED TO ${action} BLKCRD`);
    }
  };

  const getMemberStatus = (member) => {
    if (!member.expires_at) return 'active';
    return new Date(member.expires_at) > new Date() ? 'active' : 'expired';
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-black sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="BLK MRKT" className="h-10 w-10 object-cover" />
            <span className="font-mono text-xl text-white tracking-wider hidden sm:block">BLK MRKT</span>
          </Link>
          
          <div className="flex items-center gap-4">
            {user?.is_admin && (
              <Link to="/admin" className="text-zinc-400 hover:text-tiffany-500 transition-colors">
                <Shield className="w-5 h-5" />
              </Link>
            )}
            <Link to="/cart" className="text-zinc-400 hover:text-tiffany-500 transition-colors relative">
              <ShoppingCart className="w-5 h-5" />
              {itemCount > 0 && (
                <Badge className="absolute -top-2 -right-2 bg-tiffany-500 text-black text-xs min-w-[20px] h-5 flex items-center justify-center p-0">
                  {itemCount}
                </Badge>
              )}
            </Link>
            <Link to="/dashboard" className="text-zinc-400 hover:text-tiffany-500 transition-colors">
              <User className="w-5 h-5" />
            </Link>
            <button onClick={logout} className="text-zinc-400 hover:text-red-500 transition-colors">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-6">
          <Link to="/admin" className="text-zinc-500 hover:text-white transition-colors">
            <Home className="w-4 h-4" />
          </Link>
          <span className="text-zinc-600">/</span>
          <span className="font-mono text-sm text-purple-400">BLKCRD MEMBERS</span>
        </div>

        {/* Page Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <div className="bg-purple-500/20 p-3 border border-purple-500/30">
              <CreditCard className="w-8 h-8 text-purple-400" />
            </div>
            <div>
              <h1 className="font-mono text-2xl text-white">BLKCRD</h1>
              <p className="font-mono text-sm text-zinc-500">VIP MEMBERSHIP PROGRAM</p>
            </div>
          </div>
          <Button
            onClick={fetchBlkcrdUsers}
            disabled={loading}
            variant="outline"
            className="border-zinc-700 text-zinc-400 hover:text-white font-mono text-xs rounded-none"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            REFRESH
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <div className="border border-purple-500/30 bg-purple-500/5 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-purple-400" />
              <span className="font-mono text-xs text-zinc-500">TOTAL MEMBERS</span>
            </div>
            <span className="font-mono text-2xl text-white">{stats.total_members}</span>
          </div>
          <div className="border border-green-500/30 bg-green-500/5 p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-4 h-4 text-green-400" />
              <span className="font-mono text-xs text-zinc-500">ACTIVE</span>
            </div>
            <span className="font-mono text-2xl text-green-400">{stats.active_members}</span>
          </div>
          <div className="border border-red-500/30 bg-red-500/5 p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-4 h-4 text-red-400" />
              <span className="font-mono text-xs text-zinc-500">EXPIRED</span>
            </div>
            <span className="font-mono text-2xl text-red-400">{stats.expired_members}</span>
          </div>
          <div className="border border-tiffany-500/30 bg-tiffany-500/5 p-4">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-4 h-4 text-tiffany-400" />
              <span className="font-mono text-xs text-zinc-500">TOTAL SAVED</span>
            </div>
            <span className="font-mono text-2xl text-tiffany-400">${stats.total_savings.toFixed(2)}</span>
          </div>
        </div>

        {/* Add Member Section */}
        <div className="border border-purple-500/30 bg-purple-500/5 mb-8">
          <div className="p-4 border-b border-purple-500/30">
            <h2 className="font-mono text-sm text-white flex items-center gap-2">
              <Search className="w-4 h-4 text-purple-400" />
              ADD BLKCRD MEMBER
            </h2>
          </div>
          <div className="p-4">
            <div className="relative">
              <input
                type="text"
                value={blkcrdSearch}
                onChange={(e) => {
                  setBlkcrdSearch(e.target.value);
                  searchBlkcrdUsers(e.target.value);
                }}
                placeholder="Search by email or name..."
                className="w-full h-12 bg-zinc-900 border border-zinc-700 text-white font-mono text-sm px-4 focus:border-purple-500 outline-none"
              />
              {searchingBlkcrd && (
                <div className="absolute right-4 top-4">
                  <div className="w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
              
              {/* Search Results Dropdown */}
              {blkcrdSearchResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-zinc-900 border border-zinc-700 max-h-64 overflow-y-auto">
                  {blkcrdSearchResults.map(u => (
                    <button
                      key={u.id}
                      onClick={() => !u.has_blkcrd && grantBlkcrd(u.id, u.email)}
                      disabled={u.has_blkcrd}
                      className={`w-full p-4 text-left border-b border-zinc-800 last:border-0 ${
                        u.has_blkcrd 
                          ? 'opacity-50 cursor-not-allowed' 
                          : 'hover:bg-purple-500/10'
                      }`}
                    >
                      <p className="font-mono text-sm text-white">{u.email}</p>
                      <p className="font-mono text-xs text-zinc-500">{u.full_name || 'No name'}</p>
                      {u.has_blkcrd && (
                        <span className="font-mono text-[10px] text-purple-400">ALREADY HAS BLKCRD</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <p className="font-mono text-xs text-zinc-600 mt-2">
              SEARCH FOR A USER BY EMAIL OR NAME TO GRANT BLKCRD MEMBERSHIP
            </p>
          </div>
        </div>

        {/* Members List */}
        <div className="border border-zinc-800">
          <div className="p-4 border-b border-zinc-800 bg-zinc-900/50">
            <h2 className="font-mono text-sm text-white">CURRENT MEMBERS</h2>
          </div>
          
          {loading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="font-mono text-sm text-zinc-500">LOADING MEMBERS...</p>
            </div>
          ) : blkcrdUsers.length === 0 ? (
            <div className="p-8 text-center">
              <CreditCard className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
              <p className="font-mono text-sm text-zinc-500">NO BLKCRD MEMBERS YET</p>
              <p className="font-mono text-xs text-zinc-600 mt-2">USE THE SEARCH ABOVE TO ADD MEMBERS</p>
            </div>
          ) : (
            <div className="divide-y divide-zinc-800">
              {blkcrdUsers.map(member => {
                const status = getMemberStatus(member);
                const isExpired = status === 'expired';
                
                return (
                  <div 
                    key={member.id} 
                    className={`p-4 flex items-center justify-between ${isExpired ? 'bg-red-500/5' : ''}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-mono text-sm text-white truncate">{member.email}</p>
                        <span className={`font-mono text-[10px] px-2 py-0.5 ${
                          isExpired 
                            ? 'bg-red-500/20 text-red-400' 
                            : 'bg-green-500/20 text-green-400'
                        }`}>
                          {isExpired ? 'EXPIRED' : 'ACTIVE'}
                        </span>
                      </div>
                      <p className="font-mono text-xs text-zinc-500">{member.full_name || 'NO NAME'}</p>
                      
                      <div className="flex flex-wrap items-center gap-3 mt-2">
                        {member.member_since && (
                          <span className="font-mono text-[10px] flex items-center gap-1 text-zinc-400">
                            <Clock className="w-3 h-3" />
                            MEMBER SINCE: {new Date(member.member_since).toLocaleDateString()}
                          </span>
                        )}
                        {member.expires_at && (
                          <span className={`font-mono text-[10px] flex items-center gap-1 ${
                            isExpired ? 'text-red-400' : 'text-purple-400'
                          }`}>
                            <Calendar className="w-3 h-3" />
                            {isExpired ? 'EXPIRED' : 'EXPIRES'}: {new Date(member.expires_at).toLocaleDateString()}
                          </span>
                        )}
                        {member.auto_renew !== undefined && (
                          <span className={`font-mono text-[10px] flex items-center gap-1 ${
                            member.auto_renew ? 'text-green-500' : 'text-zinc-500'
                          }`}>
                            <RefreshCw className="w-3 h-3" />
                            AUTO-RENEW: {member.auto_renew ? 'ON' : 'OFF'}
                          </span>
                        )}
                        {member.current_tier && (
                          <span className="font-mono text-[10px] text-purple-500/70 flex items-center gap-1">
                            <CreditCard className="w-3 h-3" />
                            {member.current_tier.replace('_', ' ').toUpperCase()}
                          </span>
                        )}
                        {member.blkcrd_total_savings > 0 && (
                          <span className="font-mono text-[10px] text-tiffany-500 flex items-center gap-1">
                            <DollarSign className="w-3 h-3" />
                            SAVED ${member.blkcrd_total_savings.toFixed(2)}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {isExpired ? (
                        <button
                          onClick={() => setBlkcrdStatus(member.id, member.email, 'active')}
                          className="text-green-500/50 hover:text-green-500 transition-colors p-2"
                          title="Reactivate BLKCRD"
                          data-testid={`reactivate-blkcrd-${member.id}`}
                        >
                          <CheckCircle className="w-5 h-5" />
                        </button>
                      ) : (
                        <button
                          onClick={() => setBlkcrdStatus(member.id, member.email, 'expired')}
                          className="text-orange-500/50 hover:text-orange-500 transition-colors p-2"
                          title="Expire BLKCRD"
                          data-testid={`expire-blkcrd-${member.id}`}
                        >
                          <AlertCircle className="w-5 h-5" />
                        </button>
                      )}
                      <button
                        onClick={() => revokeBlkcrd(member.id, member.email)}
                        className="text-red-500/50 hover:text-red-500 transition-colors p-2"
                        title="Revoke BLKCRD (permanent)"
                        data-testid={`revoke-blkcrd-${member.id}`}
                      >
                        <XCircle className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Info Section */}
        <div className="mt-8 border border-zinc-800 p-4 bg-zinc-900/30">
          <h3 className="font-mono text-sm text-white mb-3">BLKCRD BENEFITS</h3>
          <ul className="space-y-2 font-mono text-xs text-zinc-500">
            <li className="flex items-center gap-2">
              <span className="text-purple-400">•</span>
              15% OFF ALL ORDERS (EXCLUDES GIFT CARDS, BLKCRD, AND AUCTIONS)
            </li>
            <li className="flex items-center gap-2">
              <span className="text-purple-400">•</span>
              FIRST LOOK AT "COMING SOON" DROPS
            </li>
          </ul>
        </div>
      </main>
    </div>
  );
}
